export type AnalyticsParameters = Record<string, string | number | boolean | undefined>;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const ATTRIBUTION_KEY = "lw.firstTouchAttribution";
const GOOGLE_ADS_ID = process.env.REACT_APP_GOOGLE_ADS_ID;
const PILOT_CONVERSION_LABEL = process.env.REACT_APP_GOOGLE_ADS_PILOT_CONVERSION_LABEL;
let googleAdsConfigured = false;

export interface FirstTouchAttribution {
  landingPage: string;
  referrer?: string;
  capturedAt: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  gclid?: string;
}

const readStoredAttribution = (): FirstTouchAttribution | null => {
  try {
    const value = window.localStorage.getItem(ATTRIBUTION_KEY);
    return value ? JSON.parse(value) as FirstTouchAttribution : null;
  } catch {
    return null;
  }
};

export const captureFirstTouchAttribution = (): FirstTouchAttribution => {
  const existing = readStoredAttribution();
  if (existing) return existing;

  const query = new URLSearchParams(window.location.search);
  const attribution: FirstTouchAttribution = {
    landingPage: `${window.location.pathname}${window.location.search}`,
    referrer: document.referrer || undefined,
    capturedAt: new Date().toISOString(),
    utmSource: query.get("utm_source") || undefined,
    utmMedium: query.get("utm_medium") || undefined,
    utmCampaign: query.get("utm_campaign") || undefined,
    utmTerm: query.get("utm_term") || undefined,
    utmContent: query.get("utm_content") || undefined,
    gclid: query.get("gclid") || undefined,
  };

  try {
    window.localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(attribution));
  } catch {
    // Attribution is useful, but it must never block the visitor experience.
  }

  return attribution;
};

export const getFirstTouchAttribution = (): FirstTouchAttribution =>
  readStoredAttribution() || captureFirstTouchAttribution();

const configureGoogleAds = () => {
  if (!window.gtag || !GOOGLE_ADS_ID || googleAdsConfigured) return;
  window.gtag("config", GOOGLE_ADS_ID);
  googleAdsConfigured = true;
};

export const trackEvent = (name: string, parameters: AnalyticsParameters = {}) => {
  if (!window.gtag) return;
  configureGoogleAds();
  window.gtag("event", name, parameters);
};

export const trackPageView = (path: string) => {
  captureFirstTouchAttribution();
  trackEvent("page_view", {
    page_location: window.location.href,
    page_path: path,
    page_title: document.title,
  });
};

export const trackPilotConversion = () => {
  trackEvent("generate_lead", {
    currency: "USD",
    value: 25,
    lead_type: "two_sunday_pilot",
  });

  if (!window.gtag || !GOOGLE_ADS_ID || !PILOT_CONVERSION_LABEL) return;
  configureGoogleAds();
  window.gtag("event", "conversion", {
    send_to: `${GOOGLE_ADS_ID}/${PILOT_CONVERSION_LABEL}`,
    currency: "USD",
    value: 25,
  });
};
